import {
  ActionButton,
  ActionsGroup,
  BasicInput,
  Col,
  Entidade,
  FAB,
  FormikSelect,
  InputInteger,
  PagedResponse,
  Row,
  SectionTitle,
  Table,
  Yup,
  useShowNotification
} from '@elotech/components';
import { AxiosPromise } from 'axios';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FormGroup } from 'react-bootstrap';

import { ProtocoloArquivoDTO, ProtocoloDTO } from '../../type';
import { OrigemProtocoloEnum } from '../../type/enum';
import { enumToOptions } from '../../utils';
import { InlineButton } from '../inline-button/InlineButton';
import { EntidadeSearch } from '../search';
import { TableContainer } from '../table';
import { AssinaturaQuickView } from './AssinaturaQuickView';

type Props = {
  protocolos?: ProtocoloDTO[];
  loading: boolean;
  onSearchProtocolo: (
    numero: string,
    ano: string,
    setProtocoloSearch: (protocolos: ProtocoloDTO[]) => void,
    entidade?: string | number
  ) => void;
  onAddProtocolo: (
    id?: string,
    origem?: OrigemProtocoloEnum,
    descricao?: string,
    numero?: string,
    ano?: number,
    link?: string,
    onLimparFiltros?: () => void
  ) => void;
  onFinalizar: () => void;
  onMarcarPrincipal: (id: string) => void;
  onPreviewArquivo: (fullPath: string, fileName: string) => void;
  onRemove: (id: string, onLimparFiltros: () => void) => void;
  onEntidadeSearch?: (
    search: string,
    pagination?: any,
    sort?: any
  ) => AxiosPromise<PagedResponse<Entidade>>;
  canAdd?: boolean;
  canDelete?: boolean;
  canUpdate?: boolean;
};

export const ProtocoloComponent = ({
  protocolos,
  loading,
  onSearchProtocolo,
  onAddProtocolo,
  onFinalizar,
  onMarcarPrincipal,
  onPreviewArquivo,
  onRemove,
  onEntidadeSearch,
  canAdd = true,
  canDelete = true,
  canUpdate = true
}: Props) => {
  const [entidade, setEntidade] = useState<Entidade>({
    id: '',
    brasao: '',
    cnpj: '',
    nome: '',
    principal: false
  });
  const [numero, setNumero] = useState('');
  const [ano, setAno] = useState('');
  const [arquivoQuick, setArquivoQuick] = useState<ProtocoloArquivoDTO>();
  const [protocoloSearch, setProtocoloSearch] = useState<ProtocoloDTO[]>([]);
  const [processosInternos, setProcessosInternos] = useState<ProtocoloDTO[]>(
    []
  );
  const [processosExternos, setProcessosExternos] = useState<ProtocoloDTO[]>(
    []
  );
  const showNotification = useShowNotification();

  useEffect(() => {
    setProcessosInternos([]);
    setProcessosExternos([]);

    if (protocolos?.length) {
      setProcessosInternos(protocolos.filter(p => p.id));
      setProcessosExternos(protocolos.filter(p => !p.id));
    }
  }, [protocolos]);

  const onPesquisar = () => {
    if (!numero || !ano || (onEntidadeSearch && !entidade?.id)) {
      return showNotification({
        level: 'error',
        message: onEntidadeSearch
          ? 'Informe a entidade, número e o ano para efetuar a pesquisa!'
          : 'Informe o número e o ano para efetuar a pesquisa!'
      });
    }

    onSearchProtocolo(
      numero,
      ano,
      setProtocoloSearch,
      onEntidadeSearch ? entidade?.id : undefined
    );
  };

  const onLimparFiltros = () => {
    setNumero('');
    setAno('');
    setProtocoloSearch([]);
  };

  const onViewProtocolo = (protocolo: ProtocoloDTO) => {
    const { hostname, origin } = window.location;
    const isLocalhost = hostname.includes('localhost');
    const url = `${origin}/protocolo/processos-novo/view/?tipo=${protocolo.tipo?.id}&numero=${protocolo.numero}&ano=${protocolo.ano}`;

    if (isLocalhost) {
      return showNotification({
        level: 'warning',
        message: 'Não é possível abrir o link como localhost'
      });
    }

    if (!window.open(url, '_blank')) {
      throw new Error('Sem permissão para mostrar popups');
    }
  };

  const onPreview = (protocolo: ProtocoloDTO) => {
    if (protocolo?.arquivos?.length) {
      const arquivo = protocolo?.arquivos[0];
      onPreviewArquivo(arquivo.urlS3, arquivo.nome);
    }
  };

  const onViewAssinatura = (protocolo: ProtocoloDTO) => {
    if (protocolo?.arquivos?.length) {
      setArquivoQuick(protocolo?.arquivos[0]);
    }
  };

  const showEntidadeColumn = () => {
    return onEntidadeSearch ? (
      <Table.Column
        name="entidade"
        header="Entidade"
        value={(protocolo: ProtocoloDTO) => protocolo.entidade?.id}
      />
    ) : undefined;
  };

  const renderAddProtocolo = () =>
    canAdd && (
      <>
        <SectionTitle>Pesquisar Protocolos</SectionTitle>
        <Row>
          {onEntidadeSearch ? (
            <>
              <EntidadeSearch
                size={4}
                name="entidade"
                value={entidade && entidade.id ? entidade : undefined}
                onSearch={onEntidadeSearch}
                onSelect={(_: string, value: Entidade): void => {
                  setEntidade(value);
                }}
                disableFormGroup={true}
              />
            </>
          ) : (
            <></>
          )}
          <Col md={2}>
            <label className="label">Número</label>
            <InputInteger
              value={numero}
              name="numero"
              type="text"
              size={2}
              onChange={event => setNumero(event.target!.value)}
            />
          </Col>
          <Col md={2}>
            <label className="label">Ano</label>
            <InputInteger
              value={ano}
              name="ano"
              type="text"
              size={2}
              onChange={event => setAno(event.target!.value)}
            />
          </Col>
          <Col sm={4}>
            <InlineButton
              label={'Pesquisar'}
              className="mt-xs"
              icon="fa fa-search"
              onClick={onPesquisar}
            />
            <InlineButton
              label={'Limpar'}
              className="mt-xs"
              data-testid="addButton"
              icon="fa fa-trash"
              onClick={onLimparFiltros}
            />
          </Col>
        </Row>

        <TableContainer>
          <Table values={protocoloSearch || []} loading={loading}>
            {showEntidadeColumn()}
            <Table.Column
              name="numero"
              header="Número"
              value={(protocolo: ProtocoloDTO) => protocolo.numero}
            />
            <Table.Column
              name="ano"
              header="Ano"
              value={(protocolo: ProtocoloDTO) => protocolo.ano}
            />
            <Table.Column
              name="situacao"
              header="Situação"
              value={(protocolo: ProtocoloDTO) => protocolo.situacao.descricao}
            />
            <Table.Column
              name="requerente"
              header="Requerente"
              value={(protocolo: ProtocoloDTO) => protocolo.pessoa?.nome}
            />
            <Table.Column
              name="assunto"
              header="Assunto"
              value={(protocolo: ProtocoloDTO) =>
                `${protocolo.assunto?.id} - ${protocolo.assunto?.descricao}`
              }
            />
            <Table.Column
              name="tipo"
              header="Tipo"
              value={(protocolo: ProtocoloDTO) =>
                `${protocolo.tipo?.id} - ${protocolo.tipo?.descricao}`
              }
            />
            <Table.Column
              name="observacao"
              header="Observação"
              value={(protocolo: ProtocoloDTO) => protocolo.observacao}
            />

            <Table.Column
              header=""
              value={(protocolo: ProtocoloDTO) => {
                return (
                  <ActionsGroup>
                    <ActionButton
                      key="add-button"
                      label="Vincular Protocolo"
                      icon="plus"
                      onClick={() =>
                        onAddProtocolo(
                          protocolo.uuid,
                          undefined,
                          undefined,
                          protocolo.numero?.toString(),
                          protocolo.ano,
                          undefined,
                          onLimparFiltros
                        )
                      }
                    />
                  </ActionsGroup>
                );
              }}
            />
          </Table>
        </TableContainer>
      </>
    );
  const renderAddProcessoExterno = () =>
    canAdd && (
      <Formik
        onSubmit={(values, { resetForm }) => {
          onAddProtocolo(
            undefined,
            values.origem as OrigemProtocoloEnum,
            values.descricao,
            values.numero,
            values.ano,
            values.link,
            onLimparFiltros
          );

          resetForm();
        }}
        initialValues={{
          origem: OrigemProtocoloEnum.SEI,
          descricao: '',
          numero: '',
          ano: undefined,
          link: ''
        }}
        validationSchema={Yup.object().shape({
          origem: Yup.string().required('Origem é obrigatório'),
          numero: Yup.string().required('Número é obrigatório')
        })}
      >
        {formProps => (
          <Form>
            <Row>
              <FormikSelect
                name="origem"
                label="Origem"
                getOptionValue={origem => origem.value}
                getOptionLabel={origem => origem.description}
                options={enumToOptions(OrigemProtocoloEnum)}
                size={2}
              />
              <BasicInput
                size={2}
                name="numero"
                label="Número"
                maxLength={100}
              />
              <BasicInput size={3} name="link" label="Link" maxLength={500} />
              <Col sm={2} md={2}>
                <FormGroup>
                  <InlineButton
                    className="mt-xs"
                    label="Adicionar"
                    onClick={formProps.submitForm}
                    icon="fa fa-plus"
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    );

  return (
    <>
      {renderAddProtocolo()}
      <SectionTitle>Protocolos Vinculados</SectionTitle>
      <TableContainer>
        <Table values={processosInternos} loading={loading}>
          {showEntidadeColumn()}
          <Table.Column
            name="numero"
            header="Número"
            value={(protocolo: ProtocoloDTO) => protocolo.numero}
          />
          <Table.Column
            name="ano"
            header="Ano"
            value={(protocolo: ProtocoloDTO) => protocolo.ano}
          />
          <Table.Column
            name="situacao"
            header="Situação"
            value={(protocolo: ProtocoloDTO) => protocolo.situacao.descricao}
          />
          <Table.Column
            name="requerente"
            header="Requerente"
            value={(protocolo: ProtocoloDTO) => protocolo.pessoa?.nome}
          />
          <Table.Column
            name="assunto"
            header="Assunto"
            value={(protocolo: ProtocoloDTO) =>
              `${protocolo.assunto?.id} - ${protocolo.assunto?.descricao}`
            }
          />
          <Table.Column
            name="tipo"
            header="Tipo"
            value={(protocolo: ProtocoloDTO) =>
              `${protocolo.tipo?.id} - ${protocolo.tipo?.descricao}`
            }
          />
          <Table.Column
            name="observacao"
            header="Observação"
            value={(protocolo: ProtocoloDTO) => protocolo.observacao}
          />
          <Table.Column
            name="principal"
            header="Principal"
            value={(protocolo: ProtocoloDTO) =>
              protocolo.principalVinculo ? 'Sim' : 'Não'
            }
          />
          <Table.Column
            header=""
            value={(protocolo: ProtocoloDTO) => {
              return (
                <ActionsGroup>
                  {!protocolo.principalVinculo! && canUpdate && (
                    <ActionButton
                      label="Marcar como principal"
                      icon="check"
                      onClick={() =>
                        onMarcarPrincipal(protocolo.idVinculo || '')
                      }
                    />
                  )}
                  <ActionButton
                    label="Visualizar Processo"
                    icon="eye"
                    onClick={() => onViewProtocolo(protocolo)}
                  />
                  <ActionButton
                    label="Visualizar Arquivo"
                    icon="file"
                    onClick={() => onPreview(protocolo)}
                  />
                  <ActionButton
                    icon="file-signature"
                    label="Assinaturas"
                    onClick={() => onViewAssinatura(protocolo)}
                  />
                  {canDelete && (
                    <ActionButton
                      label="Excluir"
                      icon="trash"
                      onClick={() =>
                        onRemove(protocolo.idVinculo!, onLimparFiltros)
                      }
                    />
                  )}
                </ActionsGroup>
              );
            }}
          />
        </Table>
      </TableContainer>
      <SectionTitle>Processos Externos</SectionTitle>
      {renderAddProcessoExterno()}
      <TableContainer>
        <Table values={processosExternos} loading={loading}>
          <Table.Column
            name="origem"
            header="Origem"
            value={(protocolo: ProtocoloDTO) => protocolo.origem}
          />
          <Table.Column
            name="numero"
            header="Número"
            value={(protocolo: ProtocoloDTO) => protocolo.numeroIdentificacao}
          />
          <Table.Column
            name="link"
            header="Link"
            value={(protocolo: ProtocoloDTO) => protocolo.link}
          />
          <Table.Column
            name="principal"
            header="Principal"
            value={(protocolo: ProtocoloDTO) =>
              protocolo.principalVinculo ? 'Sim' : 'Não'
            }
          />
          <Table.Column
            header=""
            value={(protocolo: ProtocoloDTO) => {
              return (
                <ActionsGroup>
                  {!protocolo.principalVinculo! && canAdd && (
                    <ActionButton
                      label="Marcar como principal"
                      icon="check"
                      onClick={() =>
                        onMarcarPrincipal(protocolo.idVinculo || '')
                      }
                    />
                  )}
                  {canDelete && (
                    <ActionButton
                      label="Excluir"
                      icon="trash"
                      onClick={() =>
                        onRemove(protocolo.idVinculo!, onLimparFiltros)
                      }
                    />
                  )}
                </ActionsGroup>
              );
            }}
          />
        </Table>
      </TableContainer>
      {arquivoQuick && (
        <AssinaturaQuickView
          arquivo={arquivoQuick}
          loading={loading}
          onClose={() => setArquivoQuick(undefined)}
        />
      )}
      <div className="btn-save">
        <FAB
          icon="check"
          iconColor="white"
          title="Finalizar"
          onClick={onFinalizar}
        />
      </div>
    </>
  );
};
